import { useEffect, useRef, useState } from 'react';

export interface CountUpProps {
  value: number;
  format: (n: number) => string;
  /** Duración en ms. */
  duration?: number;
}

/**
 * Cifra que sube desde 0 la primera vez que entra en pantalla. Siempre renderiza el valor final
 * (lectores de pantalla, tests, sin JS) y sólo anima cuando el elemento se ve y no se pidió
 * "menos movimiento".
 */
export function CountUp({ value, format, duration = 700 }: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [shown, setShown] = useState<number | null>(null);
  const done = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || done.current || typeof IntersectionObserver === 'undefined') return;
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      return;
    let raf = 0;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      done.current = true;
      const start = performance.now();
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        if (t < 1) {
          setShown(value * eased);
          raf = requestAnimationFrame(tick);
        } else {
          setShown(null);
        }
      };
      raf = requestAnimationFrame(tick);
    });
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value, duration]);

  return (
    <span ref={ref}>
      {shown === null ? (
        format(value)
      ) : (
        <>
          <span className="sr-only">{format(value)}</span>
          <span aria-hidden>{format(shown)}</span>
        </>
      )}
    </span>
  );
}
